import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { applyControlToScopes, unwrapPost } from "../../../../helpers/auditHub_helper";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_BORDER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_SUBTLE,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
} from "../../AuditHub/theme";

const NOTICE_COLOR = { success: "#16a34a", warning: "#b45309", error: "#dc2626" };

const OUTCOMES = [
  { key: "applied", title: "Applied", color: "#16a34a", hint: "The verdict and answers were copied." },
  { key: "unchanged", title: "Already up to date", color: AH_SUBTLE, hint: "These accounts already carried this verdict." },
  {
    key: "skipped",
    title: "Skipped",
    color: "#b45309",
    hint: "This control is not on these accounts' rosters. Load the framework's manual checks and try again.",
  },
  {
    key: "wrongFramework",
    title: "Different framework",
    color: "#b45309",
    hint: "These accounts are on another framework, so this verdict cannot apply to them.",
  },
  {
    key: "blocked",
    title: "Blocked",
    color: "#dc2626",
    hint: "The control's current status on these accounts does not allow this verdict. They were left unchanged.",
  },
];

const closeXSx = {
  p: 0,
  m: 0,
  border: "none",
  background: "transparent",
  color: AH_WHITE,
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  WebkitTextStroke: `0.6px ${AH_WHITE}`,
  "&:disabled": { cursor: "not-allowed", opacity: 0.6 },
};

const buttonBaseSx = {
  textTransform: "none",
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  flexShrink: 0,
  boxShadow: "none",
};

const greyButtonSx = {
  ...buttonBaseSx,
  fontWeight: 600,
  color: AH_WHITE,
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  "&:hover": { backgroundColor: "#5a6268", borderColor: "#5a6268", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: AH_INPUT_BORDER, color: "#94a3b8", borderColor: AH_INPUT_BORDER },
};

const primaryButtonSx = {
  ...buttonBaseSx,
  fontWeight: 700,
  color: AH_WHITE,
  backgroundColor: AH_ACCENT,
  "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#fed7aa", color: AH_WHITE },
};

const choiceSx = {
  "& .MuiRadio-root, & .MuiCheckbox-root": { color: "#94a3b8", p: 0.75 },
  "& .Mui-checked": { color: `${AH_ACCENT} !important` },
  "& .MuiFormControlLabel-label": { fontSize: "0.875rem", color: AH_TEXT },
  "& .MuiFormControlLabel-label.Mui-disabled": { color: "#94a3b8" },
  mr: 0,
};

export const scopeLabel = (target) => {
  if (!target) return "";
  const name = target.accountName && target.accountName !== target.accountNumber ? target.accountName : "";
  return name ? `${name} (${target.accountNumber})` : target.accountNumber || target.auditScopeId;
};

const ApplyToScopesDialog = ({
  open,
  onClose,
  auditId,
  auditControlId,
  controlCode,
  targets,
  scopeLabels,
  statusLabel,
  onApplied,
}) => {
  const [mode, setMode] = useState("all");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setMode("all");
      setSelected([]);
      setSaving(false);
      setError(null);
      setResult(null);
    }
  }, [open]);

  const list = Array.isArray(targets) ? targets : [];
  const canSubmit = !saving && !result && list.length > 0 && (mode === "all" || selected.length > 0);
  const labelFor = (scopeId) => scopeLabels?.get(scopeId) || scopeId;

  const close = () => {
    if (saving) return;
    onClose();
  };

  const toggle = (scopeId) => {
    setSelected((prev) => (prev.includes(scopeId) ? prev.filter((id) => id !== scopeId) : [...prev, scopeId]));
    if (error) setError(null);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const body = mode === "all" ? { allScopes: true } : { auditScopeIds: selected };
      setResult(unwrapPost(await applyControlToScopes(auditId, auditControlId, body)) || {});
      if (onApplied) onApplied();
    } catch (err) {
      setError(err?.message || "Unable to apply this verdict to other accounts.");
    } finally {
      setSaving(false);
    }
  };

  const appliedCount = Array.isArray(result?.applied) ? result.applied.length : 0;
  const notice = error
    ? { severity: "error", message: error }
    : result
      ? appliedCount
        ? {
            severity: "success",
            message: `Applied to ${appliedCount} account${appliedCount === 1 ? "" : "s"}.`,
          }
        : { severity: "warning", message: "No accounts were changed." }
      : null;

  const fieldId = (name) => `apply-scopes-${name}-${auditControlId}`;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      aria-labelledby={fieldId("title")}
      PaperProps={{
        component: "form",
        noValidate: true,
        onSubmit: (event) => {
          event.preventDefault();
          if (result) close();
          else submit();
        },
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "none",
          border: `1px solid ${AH_INPUT_BORDER}`,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id={fieldId("title")}
            sx={{ fontWeight: 700, fontSize: { xs: "1.125rem", sm: "1.25rem" }, color: AH_WHITE, letterSpacing: "-0.02em", lineHeight: 1.2 }}
          >
            Apply to Other Accounts
          </Typography>
          <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem" }}>
            Apply the same decision for the control ID &quot;{controlCode || "this control"}&quot; to the other accounts for this assessment.
          </Typography>
        </Box>
        <Box component="button" type="button" aria-label="Close" onClick={close} disabled={saving} sx={closeXSx}>
          <span aria-hidden="true">×</span>
        </Box>
      </Box>

      <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: AH_TRACK }}>
        <Box
          sx={{
            backgroundColor: AH_WHITE,
            border: `1px solid ${AH_INPUT_BORDER}`,
            borderRadius: "12px",
            p: 2,
          }}
        >
          {result ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {OUTCOMES.map((outcome) => {
                const raw = Array.isArray(result[outcome.key]) ? result[outcome.key] : [];
                if (!raw.length) return null;
                return (
                  <Box key={outcome.key}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: outcome.color }}>
                      {outcome.title} ({raw.length})
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25, lineHeight: 1.5 }}>{outcome.hint}</Typography>
                    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                      {raw.map((entry) => {
                        const scopeId = typeof entry === "string" ? entry : entry?.auditScopeId;
                        return (
                          <Typography component="li" key={scopeId} sx={{ fontSize: 13, color: AH_TEXT, lineHeight: 1.6, overflowWrap: "anywhere" }}>
                            {labelFor(scopeId)}
                            {outcome.key === "blocked" && entry?.reviewStatus ? (
                              <Box component="span" sx={{ color: AH_MUTED }}>
                                {" "}
                                — currently {statusLabel(entry.reviewStatus)}
                              </Box>
                            ) : null}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
              {Number(result.answersCopied) > 0 ? (
                <Typography sx={{ fontSize: 12, color: AH_MUTED, borderTop: `1px solid ${AH_BORDER}`, pt: 1.25 }}>
                  {result.answersCopied} question answer{Number(result.answersCopied) === 1 ? "" : "s"} copied.
                </Typography>
              ) : null}
            </Box>
          ) : (
            <>
              <RadioGroup
                value={mode}
                onChange={(event) => {
                  setMode(event.target.value);
                  if (error) setError(null);
                }}
                aria-label="Accounts to apply to"
              >
                <FormControlLabel
                  value="all"
                  disabled={saving}
                  control={<Radio size="small" />}
                  label={`All other accounts on this framework (${list.length})`}
                  sx={choiceSx}
                />
                <FormControlLabel
                  value="selected"
                  disabled={saving}
                  control={<Radio size="small" />}
                  label="Selected accounts"
                  sx={choiceSx}
                />
              </RadioGroup>
              <Box
                role="group"
                aria-label="Select accounts"
                sx={{
                  ml: 3.5,
                  mt: 0.5,
                  maxHeight: 240,
                  overflowY: "auto",
                  border: `1px solid ${AH_BORDER}`,
                  borderRadius: "8px",
                  px: 1,
                  py: 0.5,
                  display: "flex",
                  flexDirection: "column",
                  opacity: mode === "selected" ? 1 : 0.6,
                }}
              >
                {list.map((target) => (
                  <FormControlLabel
                    key={target.auditScopeId}
                    disabled={saving || mode !== "selected"}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(target.auditScopeId)}
                        onChange={() => toggle(target.auditScopeId)}
                      />
                    }
                    label={scopeLabel(target)}
                    sx={{ ...choiceSx, "& .MuiFormControlLabel-label": { ...choiceSx["& .MuiFormControlLabel-label"], overflowWrap: "anywhere" } }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{
          px: 2.5,
          py: 1.5,
          gap: 1.5,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          borderTop: `1px solid ${AH_INPUT_BORDER}`,
          bgcolor: AH_WHITE,
          justifyContent: "space-between",
        }}
      >
        <Button type="button" variant="contained" disableElevation onClick={close} disabled={saving} sx={greyButtonSx}>
          Cancel
        </Button>
        {notice ? (
          <Typography
            role={notice.severity === "error" ? "alert" : "status"}
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: NOTICE_COLOR[notice.severity],
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {notice.message}
          </Typography>
        ) : null}
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={result ? false : !canSubmit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={primaryButtonSx}
        >
          {result ? "Done" : saving ? "Applying..." : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyToScopesDialog;
